"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ENS = exports.FDSRegistrarContract = exports.PublicResolverContract = exports.ENSRegistryContract = void 0;
const ethers_1 = require("ethers");
const constants_1 = require("../constants/constants");
const environment_1 = require("../constants/environment");
const tx_1 = require("../utils/tx");
const keys_1 = require("../utils/keys");
const environments_enum_1 = require("../model/environments.enum");
const ENSRegistry_json_1 = __importDefault(require("../contracts/ENSRegistry/ENSRegistry.json"));
const PublicResolver_json_1 = __importDefault(require("../contracts/PublicResolver/PublicResolver.json"));
const FDSRegistrar_json_1 = __importDefault(require("../contracts/FDSRegistrar/FDSRegistrar.json"));
const domains_1 = require("../utils/domains");
const blockchain_1 = require("../utils/blockchain");
const { keccak256, toUtf8Bytes, namehash } = ethers_1.utils;
exports.ENSRegistryContract = ENSRegistry_json_1.default;
exports.PublicResolverContract = PublicResolver_json_1.default;
exports.FDSRegistrarContract = FDSRegistrar_json_1.default;
const MIN_BALANCE = ethers_1.BigNumber.from('10000000000000000');
/**
 * ENS Class
 * Provides interface for interaction with the ENS smart contracts
 */
class ENS {
    constructor(config = environment_1.ENVIRONMENT_CONFIGS[environments_enum_1.Environments.LOCALHOST], signerOrProvider = null, domain = constants_1.ENS_DOMAIN) {
        this.config = config;
        this.domain = domain;
        this._provider = new ethers_1.providers.JsonRpcProvider(config.rpcUrl);
        const { ensRegistry, fdsRegistrar, publicResolver } = config.contractAddresses;
        this._ensRegistryContract = new ethers_1.Contract(ensRegistry, exports.ENSRegistryContract.abi, this._provider);
        this._publicResolverContract = new ethers_1.Contract(publicResolver, exports.PublicResolverContract.abi, this._provider);
        this._fdsRegistrarContract = new ethers_1.Contract(fdsRegistrar, exports.FDSRegistrarContract.abi, this._provider);
        if (signerOrProvider) {
            this.connect(signerOrProvider);
        }
    }
    /**
     * returns RPC provider
     */
    get provider() {
        return this._provider;
    }
    /**
     * Connects signer to the smart contracts
     * @param signerOrProvider An instance of ethers.js Wallet or any other signer
     */
    connect(signerOrProvider) {
        this._publicResolverContract = this._publicResolverContract.connect(signerOrProvider);
        this._fdsRegistrarContract = this._fdsRegistrarContract.connect(signerOrProvider);
        this._ensRegistryContract = this._ensRegistryContract.connect(signerOrProvider);
    }
    /**
     * Returns owner address of the provided username on ENS
     * @param username ENS username
     * @returns owner's address
     */
    getUsernameOwner(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, domains_1.assertUsername)(username);
            const usernameHash = this.hashUsername(username);
            return this._ensRegistryContract.owner(usernameHash);
        });
    }
    /**
     * Checks whether username is available on ENS
     * @param username ENS username
     * @returns True if the username is available
     */
    isUsernameAvailable(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, domains_1.assertUsername)(username);
            const owner = yield this.getUsernameOwner(username);
            return owner === constants_1.NULL_ADDRESS;
        });
    }
    /**
     * Sets owner of the provided username on ENS
     * @param username ENS username
     * @param address Owner address of the username
     * @param publicKey Hex string of a public key
     */
    registerUsername(username, address, publicKey, expires = 86400) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                (0, domains_1.assertUsername)(username);
                let ownerAddress = constants_1.NULL_ADDRESS;
                if (this.config.performChecks) {
                    yield (0, blockchain_1.checkMinBalance)(this.provider, address, MIN_BALANCE);
                    ownerAddress = yield this.getUsernameOwner(username);
                    if (ownerAddress !== constants_1.NULL_ADDRESS) {
                        throw new Error(`ENS: Username ${username} is not available`);
                    }
                }
                if (ownerAddress === constants_1.NULL_ADDRESS) {
                    yield (0, tx_1.waitTransaction)(this._fdsRegistrarContract.register(keccak256(toUtf8Bytes(username)), address, expires));
                }
                yield (0, tx_1.waitTransaction)(this._ensRegistryContract.setResolver(this.hashUsername(username), this._publicResolverContract.address));
                yield this.setPublicKey(username, publicKey);
            }
            catch (error) {
                if ((0, blockchain_1.isTxError)(error)) {
                    throw new Error((0, blockchain_1.extractMessageFromFailedTx)(error));
                }
                throw error;
            }
        });
    }
    /**
     * Returns public key of registered username
     * @param username
     * @returns public key
     */
    getPublicKey(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, domains_1.assertUsername)(username);
            const [publicKeyX, publicKeyY] = yield this._publicResolverContract.pubkey(this.hashUsername(username));
            const publicKey = (0, keys_1.joinPublicKey)(publicKeyX, publicKeyY);
            if (!(0, keys_1.isPublicKeyValid)(publicKey)) {
                throw new Error('Public key is not set or is invalid');
            }
            return publicKey;
        });
    }
    /**
     * Fetches all ENS data related to the provided username
     * @param username ENS username
     * @returns All user's data stored on ENS
     */
    getUserData(username) {
        (0, domains_1.assertUsername)(username);
        return this._publicResolverContract.getAll(this.hashUsername(username));
    }
    /**
     * Sets user's public key to the user's ENS entry
     * @param username ENS username
     * @param publicKey Public key that will be added to ENS
     */
    setPublicKey(username, publicKey) {
        (0, domains_1.assertUsername)(username);
        const [publicKeyX, publicKeyY] = (0, keys_1.splitPublicKey)(publicKey);
        return (0, tx_1.waitTransaction)(this._publicResolverContract.setPubkey(this.hashUsername(username), publicKeyX, publicKeyY));
    }
    hashUsername(username) {
        return namehash(`${username}.${this.domain}`);
    }
}
exports.ENS = ENS;
exports.default = ENS;
